import React, { useMemo } from 'react';
import { RichTextDescription } from 'coop-adventure-engine';
import Button from '../ui/Button';
import RichTextRenderer from '../ui/RichTextRenderer';
import { isRichTextDescription, plainTextToRichText } from '../../utils/richTextRenderer';

export interface OptionButtonProps {
  text: string | RichTextDescription;
  onClick: () => void;
  disabled?: boolean;
  waiting?: boolean;
  disabledReason?: string; // Optional reason why the option is disabled
  variant?: 'default' | 'primary' | 'secondary' | 'glass-primary' | 'glass-secondary' | 'glass-ghost';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  className?: string;
  customColor?: string; // Optional custom button color from parent
}

function resolveOptionRichText(text: string | RichTextDescription): RichTextDescription {
  if (isRichTextDescription(text)) {
    return text;
  }
  return plainTextToRichText(text.replace(/^["']|["']$/g, ''));
}

function resolveOptionPlainText(text: string): string {
  return text.replace(/^["']|["']$/g, '');
}

const OptionButton: React.FC<OptionButtonProps> = ({
  text,
  onClick,
  disabled = false,
  waiting = false,
  disabledReason,
  variant = 'default',
  size = 'md',
  fullWidth = true,
  className = '',
  customColor
}) => {
  const buttonVariant = variant === 'default' ? 'glass-secondary' : variant;
  const plainText = useMemo(() => (typeof text === 'string' ? resolveOptionPlainText(text) : null), [text]);

  const description = useMemo(() => resolveOptionRichText(text), [text]);

  const richTextInner = (
    <RichTextRenderer
      description={description}
      {...(customColor ? { customColor } : {})}
      disableInlineOptions={disabled || waiting}
      className="font-medium option-rich-text w-full"
    />
  );

  return (
    <div className={`space-y-2 ${className}`}>
      <Button
        variant={buttonVariant}
        size={size}
        fullWidth={fullWidth}
        disabled={waiting ? false : disabled}
        onClick={onClick}
        {...(customColor ? { customColor } : {})}
        className={`min-h-[48px] text-center justify-center !items-stretch${waiting ? ' pointer-events-none cursor-default brightness-75' : ''}`}
        data-testid="game-option"
      >
        <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center self-stretch">
          <div className="flex w-full min-h-0 flex-col items-center justify-center">
            {plainText !== null ? <span className="font-medium option-rich-text">{plainText}</span> : richTextInner}
          </div>
          {disabled && disabledReason && !waiting && (
            <span className="text-sm text-gray-500 mt-1 text-center">
              {disabledReason}
            </span>
          )}
        </div>
      </Button>

      {waiting && (
        <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
          <div className="animate-pulse w-2 h-2 bg-blue-500 rounded-full" />
          <div
            className="animate-pulse w-2 h-2 bg-blue-500 rounded-full"
            style={{ animationDelay: '0.2s' }}
          />
          <div
            className="animate-pulse w-2 h-2 bg-blue-500 rounded-full"
            style={{ animationDelay: '0.4s' }}
          />
        </div>
      )}
    </div>
  );
};

export default OptionButton;
